import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { api } from '../../api/endpoints.js';
import { Page } from '../../components/AppShell.jsx';
import { Badge, Card, DataTable, Input, Pagination, Select, Tabs } from '../../components/ui.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import { useListQuery } from '../../hooks/useListQuery.js';
import { dateNumeric, number, phoneWithCountryCode, relative } from '../../lib/format.js';
import { PERMISSIONS } from '../../lib/permissions.js';

/** "Bristol, Bath and 2 more" — the areas a broker actually works. */
function areasLabel(areasServed = []) {
  const cities = [...new Set(areasServed.map((area) => area.city).filter(Boolean))];
  if (!cities.length) return null;
  if (cities.length <= 2) return cities.join(', ');
  return `${cities.slice(0, 2).join(', ')} and ${cities.length - 2} more`;
}

/**
 * Brokers: the agencies, and the broker accounts that have none yet.
 *
 * A broker account exists from the first sign-in, but the agency only once
 * onboarding is submitted — so the dashboard's broker count can be higher than
 * the roster. The second tab is that difference, which otherwise appears
 * nowhere in the panel. It reads the user list, so it needs user access too.
 */
export default function BrokersPage() {
  const { can } = useAuth();
  const canSeeUnfinished = can(PERMISSIONS.USER_VIEW);

  const [params, setParams] = useSearchParams();
  const tab = canSeeUnfinished && params.get('tab') === 'unfinished' ? 'unfinished' : 'agencies';

  // Only the total, for the tab's count; the tab itself pages its own list.
  const unfinishedCount = useQuery({
    queryKey: ['admin-brokers-unfinished-count'],
    queryFn: () => api.broker.unfinished({ page: 1, pageSize: 1 }),
    enabled: canSeeUnfinished,
    staleTime: 15_000,
  });

  return (
    <Page title="Brokers" subtitle="The agencies letting property through Grihvaas.">
      {canSeeUnfinished ? (
        <Tabs
          tabs={[
            { value: 'agencies', label: 'Agencies' },
            {
              value: 'unfinished',
              label: 'Onboarding not finished',
              count: unfinishedCount.data?.meta?.total,
            },
          ]}
          value={tab}
          label="Broker views"
          onChange={(value) => setParams(value === 'agencies' ? {} : { tab: value })}
        />
      ) : null}

      {tab === 'unfinished' ? <UnfinishedTab /> : <AgenciesTab />}
    </Page>
  );
}

/**
 * The broker roster.
 *
 * Scanning only: a row says which agencies need attention and opens the one
 * that does. Verifying or suspending happens on the broker's own screen, where
 * the evidence for the decision is on the same page as the button.
 */
function AgenciesTab() {
  const navigate = useNavigate();

  const list = useListQuery({
    key: 'admin-brokers',
    fetcher: api.broker.brokers,
    initialFilters: { verification: '', accountStatus: '', featured: '', visibility: 'visible' },
    searchable: true,
  });

  const columns = [
    {
      key: 'business',
      header: 'Broker',
      render: (broker) => (
        <div>
          <Link className="table__link table__primary" to={`/brokers/${broker._id}`}>
            {broker.businessName}
          </Link>
          <div className="table__muted">
            {broker.registrationNumber ? (
              <span className="mono">{broker.registrationNumber}</span>
            ) : (
              'No registration number'
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'contact',
      header: 'Contact',
      render: (broker) => (
        <div>
          <div>{broker.user?.name || '—'}</div>
          <div className="table__muted mono">{phoneWithCountryCode(broker.phone)}</div>
        </div>
      ),
    },
    {
      key: 'areas',
      header: 'Areas served',
      render: (broker) => <span className="table__muted">{areasLabel(broker.areasServed) || 'Not set'}</span>,
    },
    {
      key: 'verification',
      header: 'Verification',
      render: (broker) => <Badge status={broker.verification?.status} />,
    },
    {
      key: 'status',
      header: 'Account',
      render: (broker) => (
        <div className="row">
          <Badge status={broker.status} />
          {broker.user?.hiddenFromPublic ? <Badge tone="warning" label="Hidden" /> : null}
        </div>
      ),
    },
    {
      key: 'credits',
      header: 'Credits',
      className: 'table__numeric',
      render: (broker) => number(broker.user?.creditBalance || 0),
    },
    {
      key: 'listings',
      header: 'Live',
      className: 'table__numeric',
      render: (broker) => number(broker.stats?.activeListings || 0),
    },
  ];

  return (
    <Card bodyless>
      <div className="card__body">
        <div className="toolbar">
          <div className="toolbar__search">
            <Input
              placeholder="Search by business name or registration number"
              value={list.search}
              onChange={(event) => list.setSearch(event.target.value)}
              aria-label="Search brokers"
            />
          </div>

          <Select
            value={list.filters.verification}
            onChange={(event) => list.setFilter('verification', event.target.value)}
            aria-label="Filter by verification"
          >
            <option value="">Any verification</option>
            <option value="unverified">Unverified</option>
            <option value="pending">Pending</option>
            <option value="verified">Verified</option>
            <option value="rejected">Rejected</option>
          </Select>

          <Select
            value={list.filters.accountStatus}
            onChange={(event) => list.setFilter('accountStatus', event.target.value)}
            aria-label="Filter by account status"
          >
            <option value="">Any account</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="archived">Archived</option>
          </Select>

          <Select
            value={list.filters.featured}
            onChange={(event) => list.setFilter('featured', event.target.value)}
            aria-label="Filter by featured profile"
          >
            <option value="">Featured or not</option>
            <option value="true">Featured now</option>
          </Select>

          <Select
            value={list.filters.visibility}
            onChange={(event) => list.setFilter('visibility', event.target.value)}
            aria-label="Visible or hidden from real users"
          >
            <option value="visible">Visible to real users</option>
            <option value="hidden">Hidden from real users</option>
            <option value="all">Visible and hidden</option>
          </Select>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={list.rows}
        query={list.query}
        onRowClick={(broker) => navigate(`/brokers/${broker._id}`)}
        empty={{
          title: 'No brokers here',
          body: 'No agency matches these filters. Clear them if you expected to see one.',
        }}
      />
      <Pagination meta={list.meta} page={list.page} onPageChange={list.setPage} />
    </Card>
  );
}

/**
 * Broker accounts with no agency: they verified a sign-in code and stopped
 * before submitting onboarding. The app sends them back to that form the next
 * time they open it, so nothing here needs doing beyond, perhaps, a call.
 */
function UnfinishedTab() {
  const navigate = useNavigate();

  const list = useListQuery({
    key: 'admin-brokers-unfinished',
    fetcher: api.broker.unfinished,
    initialFilters: { visibility: 'visible' },
    searchable: true,
  });

  const columns = [
    {
      key: 'person',
      header: 'Account',
      render: (user) => (
        <div>
          <Link className="table__link table__primary" to={`/users/${user._id}`}>
            {user.name || 'No name yet'}
          </Link>
          <div className="table__muted mono">{phoneWithCountryCode(user.phone)}</div>
        </div>
      ),
    },
    {
      key: 'stage',
      header: 'Stopped at',
      render: (user) => <Badge tone="warning" label={user.profileCompletedAt ? 'Agency details' : 'Name'} />,
    },
    {
      key: 'status',
      header: 'Account',
      render: (user) => (
        <div className="row">
          <Badge status={user.status} />
          {user.hiddenFromPublic ? <Badge tone="warning" label="Hidden" /> : null}
        </div>
      ),
    },
    {
      key: 'joined',
      header: 'Signed up',
      render: (user) => <span className="table__muted">{dateNumeric(user.createdAt)}</span>,
    },
    {
      key: 'seen',
      header: 'Last seen',
      render: (user) => (
        <span className="table__muted">{user.lastLoginAt ? relative(user.lastLoginAt) : 'Never'}</span>
      ),
    },
  ];

  return (
    <Card bodyless>
      <div className="card__body">
        <div className="toolbar">
          <div className="toolbar__search">
            <Input
              placeholder="Search by name or phone"
              value={list.search}
              onChange={(event) => list.setSearch(event.target.value)}
              aria-label="Search unfinished broker accounts"
            />
          </div>

          <Select
            value={list.filters.visibility}
            onChange={(event) => list.setFilter('visibility', event.target.value)}
            aria-label="Visible or hidden from real users"
          >
            <option value="visible">Visible to real users</option>
            <option value="hidden">Hidden from real users</option>
            <option value="all">Visible and hidden</option>
          </Select>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={list.rows}
        query={list.query}
        onRowClick={(user) => navigate(`/users/${user._id}`)}
        empty={{
          title: 'Everyone has finished',
          body: 'Every broker account that signed in has an agency on the roster.',
        }}
      />
      <Pagination meta={list.meta} page={list.page} onPageChange={list.setPage} />
    </Card>
  );
}
